import * as HLF from "html-living-framework";
import {Dict} from "../core/Reflection";
import {GameManager} from "../managers/GameManager";
import {BookId} from "./BookId";
import {BooksData} from "./BooksData";
import {CharacterType} from "./CharacterType";
import {AnyStoryModel, SceneAudio, StoryId, StoryInjection, StoryVO} from "./StoryModel";

const fontGrandstander = new HLF.FontAsset("grandstander");
const characters = {
    [CharacterType.Brad]: new HLF.SpineAsset("brad_HH", "spine/brad_HH@x.skel"),
    [CharacterType.Xavier]: new HLF.SpineAsset("xavier_HH", "spine/xavier_HH@x.skel"),
    [CharacterType.Yadina]: new HLF.SpineAsset("yadina_HH", "spine/yadina_HH@x.skel"),
};

export const Assets = {
    boot: [
        new HLF.TextureAtlasAsset("boot-1"),
        new HLF.TextureAsset("splash"),
        fontGrandstander,
    ],
    fontGrandstander,
    common: [
        ...HLF.AssetFactory({"@1x": 1, "@2x": [1, 1]}, (i) => new HLF.TextureAtlasAsset(`ui-${i}`)),
    ],
    storySelect: [
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 3],
        }, (i) => new HLF.TextureAtlasAsset(`book-animation-${i}`)),
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 2],
        }, (i) => new HLF.TextureAtlasAsset(`story-select-${i}`)),
        new HLF.TextureAsset("storyselect-background"),
        new HLF.TextureAsset("storyselect-background-blurred"),
        characters[CharacterType.Xavier],
        characters[CharacterType.Yadina],
        characters[CharacterType.Brad],
        ...BooksData.reduce<HLF.AudioAsset[]>((a, t) => {
            addStoryVO(a, t.story.introVO);
            addStoryVO(a, t.story.outroVO);
            addStoryVO(a, t.storyVO);
            addStoryVO(a, t.authorVO);
            return a;
        }, []),
    ],
    sceneCreator: HLF.AssetFactory({
        "@1x": 1,
        "@2x": [1, 3],
    }, (i) => new HLF.TextureAtlasAsset(`scene-creator-${i}`)),
    bookColorRamps: BooksData.map((it) =>
        new HLF.TextureAsset(`color-ramp-book-${it.bookId}`, `images/static/color-ramps/color-ramp-book-${it.bookId}.png`)),
    paperBackground: new HLF.TextureAsset("paper-background"),
    characters,
    audio: {
        bgmMenu: new HLF.AudioAsset("backgroundmusic-menus", HLF.AudioCategory.BGM),
        bgmStory: new HLF.AudioAsset("backgroundmusic-story", HLF.AudioCategory.BGM),
        buttonPress: new HLF.AudioAsset("button-press", HLF.AudioCategory.SFX),
        bookSlide: new HLF.AudioAsset("book-slide", HLF.AudioCategory.SFX),
        bookOpen: new HLF.AudioAsset("book-open", HLF.AudioCategory.SFX),
        libraryIntroVO: ["menu_xavier_001", "menu_yadina_002", "menu_brad_001"].map((it) => new HLF.AudioAsset(it, HLF.AudioCategory.VO)),
        menuIdle: [
            "menu_xavier_002",
            "menu_xavier_003",
            "menu_brad_002",
            "menu_brad_003",
            "menu_yadina_004",
            "menu_yadina_005",
        ].map((it) => new HLF.AudioAsset(it, HLF.AudioCategory.VO)),
        storyIdle: [
            "re_xavier_001_alt",
            "re_xavier_002",
            "re_brad_001",
            "re_brad_002",
            "re_yadina_001",
            "re_yadina_002",
        ].map((it) => new HLF.AudioAsset(it, HLF.AudioCategory.VO)),
    },
};

export const StoryAssets: Dict<BookId, Array<HLF.IAsset<any>>> = {
    [BookId.Bronte]: [
        ...HLF.AssetFactory({
            "@1x": [1, 2],
            "@2x": [1, 5],
        }, (i) => new HLF.TextureAtlasAsset(`story-bronte-sisters-${i}`)),
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 1],
        }, (i) => new HLF.TextureAtlasAsset(`options-bronte-sisters-${i}`)),
    ],
    [BookId.Shelley]: [
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 3],
        }, (i) => new HLF.TextureAtlasAsset(`story-mary-shelley-${i}`)),
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 1],
        }, (i) => new HLF.TextureAtlasAsset(`options-mary-shelley-${i}`)),
    ],
    [BookId.Dickens]: [
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 1],
        }, (i) => new HLF.TextureAtlasAsset(`options-charles-dickens-${i}`)),
        ...HLF.AssetFactory({
            "@1x": [1, 2],
            "@2x": [1, 6],
        }, (i) => new HLF.TextureAtlasAsset(`story-charles-dickens-${i}`)),
    ],
    [BookId.Hurston]: [
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 1],
        }, (i) => new HLF.TextureAtlasAsset(`options-zora-hurston-${i}`)),
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 3],
        }, (i) => new HLF.TextureAtlasAsset(`story-zora-hurston-${i}`)),
    ],
    [BookId.Bly]: [
        ...HLF.AssetFactory({
            "@1x": [1, 1],
            "@2x": [1, 1],
        }, (i) => new HLF.TextureAtlasAsset(`options-nellie-bly-${i}`)),
        ...HLF.AssetFactory({
            "@1x": [1, 2],
            "@2x": [1, 5],
        }, (i) => new HLF.TextureAtlasAsset(`story-nellie-bly-${i}`)),
    ],
};

const storyAudioAssets: Dict<StoryId, HLF.AudioAsset[]> = {};

export function StoryAudioAssets(story: AnyStoryModel) {
    if (!storyAudioAssets[story.storyId]) {
        const names: Dict<string, boolean> = {};
        const assets: HLF.AudioAsset[] = [];
        const addAsset = (category: string, asset?: SceneAudio) => {
            if (asset) {
                if (typeof asset === "string") {
                    if (!names[asset]) {
                        assets.push(new HLF.AudioAsset(asset, category));
                    }
                } else if (Array.isArray(asset)) {
                    for (const a of asset) {
                        if (typeof a === "string") {
                            addAsset(category, a);
                        }
                    }
                } else {
                    for (const a of Object.getOwnPropertyNames(asset)) {
                        addAsset(category, a);
                    }
                }
            }
        };
        const gameManager = GameManager.instance;
        for (const scene of story.scenes) {
            addAsset(HLF.AudioCategory.SFX, scene.sfx);
            addAsset(HLF.AudioCategory.VO, gameManager.getScenePromptVO(scene));
            for (let i = 0; i < scene.options.length; i++) {
                addAsset(HLF.AudioCategory.SFX, scene.options[i].sfx);
                addAsset(HLF.AudioCategory.VO, gameManager.getSceneOptionVO(scene, i));
            }
        }
        storyAudioAssets[story.storyId] = assets;
    }
    return storyAudioAssets[story.storyId]!;
}

const storyInjectionAssets: Dict<StoryId, Array<HLF.IAsset<any>>> = {};

export function StoryInjectionAssets(story: AnyStoryModel) {
    if (!storyInjectionAssets[story.storyId]) {
        const assets: Array<HLF.IAsset<any>> = [];
        for (const scene of story.scenes) {
            if (scene.injections) {
                for (const p in scene.injections) {
                    if (scene.injections.hasOwnProperty(p)) {
                        for (const a of ((scene.injections as any)[p] as StoryInjection).getAssets()) {
                            if (assets.indexOf(a) < 0) {
                                assets.push(a);
                            }
                        }
                    }
                }
            }
        }
        storyInjectionAssets[story.storyId] = assets;
    }
    return storyInjectionAssets[story.storyId]!;
}

function addStoryVO(array: HLF.AudioAsset[], vo?: StoryVO) {
    if (typeof vo === "string") {
        array.push(new HLF.AudioAsset(vo, HLF.AudioCategory.VO));
    } else if (vo) {
        array.push(...vo.filter((it) => typeof it === "string").map((it) => new HLF.AudioAsset(it as string, HLF.AudioCategory.VO)));
    }
}
